import {
  answerTypesWithOptions,
  EventFormQuestionAnswerType,
  eventFormQuestionAnswerTypeValues,
  eventTypeTypeValues,
} from "@ac-expo/api-types";
import { z } from "zod";

const eventFormQuestionFields = {
  theme: z
    .string()
    .min(1, "Le thème est requis")
    .max(128, "Le thème ne doit pas dépasser 128 caractères"),
  question: z
    .string()
    .min(1, "La question est requise")
    .max(128, "La question ne doit pas dépasser 128 caractères"),
  answerType: z.enum([...eventFormQuestionAnswerTypeValues]),
  options: z
    .array(
      z.object({
        label: z
          .string()
          .min(1, "Le libellé de l'option est requis")
          .max(
            128,
            "Le libellé de l'option ne doit pas dépasser 128 caractères"
          ),
        order: z.number().int().min(0),
      })
    )
    .optional(),
};

const allowedEventTypesSchema = z.array(z.enum([...eventTypeTypeValues]));

const hasOptionsForChoiceAnswers = (data: {
  answerType: EventFormQuestionAnswerType;
  options?: unknown[];
}) =>
  !answerTypesWithOptions.includes(data.answerType) ||
  (data.options !== undefined && data.options.length > 0);

const optionsRequiredIssue = {
  message: "Une option est requise pour ce type de réponse",
  path: ["options", "root"],
};

export const eventFormQuestionSchema = z
  .object({
    ...eventFormQuestionFields,
    allowedEventTypes: allowedEventTypesSchema.min(
      1,
      "Au moins un type d'événement doit être sélectionné"
    ),
  })
  .refine(hasOptionsForChoiceAnswers, optionsRequiredIssue);

// For forms rendered without the event types field: the array stays empty
// and must not block submission.
export const eventFormQuestionWithoutEventTypesSchema = z
  .object({
    ...eventFormQuestionFields,
    allowedEventTypes: allowedEventTypesSchema,
  })
  .refine(hasOptionsForChoiceAnswers, optionsRequiredIssue);

// Organiser questions are scoped to one event: the backend derives the allowed
// event types from the event, so the payload carries none.
export const organiserQuestionSchema = z
  .object(eventFormQuestionFields)
  .refine(hasOptionsForChoiceAnswers, optionsRequiredIssue);

export type EventFormQuestionForm = z.infer<typeof eventFormQuestionSchema>;
export type OrganiserQuestionForm = z.infer<typeof organiserQuestionSchema>;

export const defaultEventFormQuestionValues: EventFormQuestionForm = {
  theme: "",
  allowedEventTypes: [],
  question: "",
  answerType: "short_text",
  options: [],
};
